import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import MaintenanceMessage from '../maintenance_message';
import LoadingSpinner from '../loading_spinner';

const RETRY_INTERVAL_MS = 500; // 0.5 seconds base interval
const MAX_RETRIES = 15; // ~2 minutes total with exponential backoff

function V2SyncGate({ children }) {
  // "checking" | "ready" | "pending_sync" | "failed"
  const [state, setState] = useState('checking');
  const retryCount = useRef(0);
  const retryTimer = useRef(null);

  const pollServer = useCallback(async () => {
    try {
      const res = await fetch('/session_api/v2_token_check', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      // Parse the JSON body to verify we got the expected contract
      // (not a redirect-followed HTML page)
      const data = res.headers.get('content-type')?.includes('application/json')
        ? await res.json()
        : null;

      if (data?.status === 'ready') {
        setState('ready');
        return;
      }

      if (data?.status === 'pending_sync' && retryCount.current < MAX_RETRIES) {
        setState('pending_sync');
        retryCount.current += 1;
        retryTimer.current = setTimeout(pollServer, RETRY_INTERVAL_MS * 1.35 ** retryCount.current);
        return;
      }

      // error, unexpected response, or max retries exceeded
      setState('failed');
    } catch {
      setState('failed');
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      if (window?.v2HealthCheck) {
        const result = await window.v2HealthCheck();
        if (result === true) {
          setState('ready'); // Token exists and V2 healthy
        } else if (result === 'no_token') {
          pollServer(); // No token — start polling the server
        } else {
          setState('failed'); // V2 unhealthy
        }
      } else {
        setState('ready');
      }
    };
    run();
    return () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, [pollServer]);

  if (state === 'ready') return children;

  if (state === 'checking' || state === 'pending_sync') {
    const message = state === 'pending_sync'
      ? 'Setting up your account \u2014 this may take a moment...'
      : 'Loading...';

    return <LoadingSpinner message={message} />;
  }

  // "failed" — the wrapper carries the new-request page's scoped styles (see
  // styles/pages/new_reporter_request_v2.scss)
  return (
    <div className="new-request-body">
      <MaintenanceMessage />
    </div>
  );
}

V2SyncGate.propTypes = {
  children: PropTypes.node.isRequired,
};

export default V2SyncGate;
